import { EventSystem, IEventSystem } from "@dhx/ts-common/events";

import { Loader } from "./datacollection/loader";
import { Sort } from "./datacollection/sort";
import { DataProxy } from "./dataproxy";
import { dhxError, dhxWarning, findByConf, isDebug, isEqualObj, copyWithoutInner, toDataDriver } from "./helpers";
import {
	DataCallback, DataEvents, Id, IDataChangeStack, IDataCollection, IDataItem,
	IDataProxy, IFilterCallback, IFilterConfig, IFilterMode, ISortMode, ITreeCollection, IUpdateObject, ReduceCallBack, Statuses, IDataEventsHandlersMap, DataDriver,
} from "./types";

import { copy, extend, findIndex, uid } from "@dhx/ts-common/core";
import { TreeCollection } from "./treecollection";

export class DataCollection<T extends IDataItem = IDataItem> implements IDataCollection<T> {
	public loadData: Promise<any>;
	public saveData: Promise<any>;
	public config: any; // [TODO] add typings
	public events: IEventSystem<DataEvents, IDataEventsHandlersMap>;

	protected _order: T[];
	protected _pull: { [id: string]: T };
	protected _sort: Sort;
	protected _filters:any;

	private _changes: IDataChangeStack;

	private _initOrder: T[];

	private _loader: Loader;

	constructor(config?: any, events?:IEventSystem<any>) {
		this.config = config || {};

		this._order = [];
		this._pull = {};
		this._changes = {order: []};
		this._initOrder = null;

		this._sort = new Sort();
		this._loader = new Loader(this, this._changes);
		this.events = events || new EventSystem<any>(this);
		this.events.on(DataEvents.loadError, (response) => {
			if (typeof response !== "string") {
				dhxError(response);
			} else {
				dhxWarning(response);
			}
		});
	}
	add(obj: IDataItem, index?: number): Id;
	add(obj: IDataItem[], index?: number): Id[];
	add(obj: IDataItem | IDataItem[], index?: number): Id | Id[] {
		if (!this.events.fire(DataEvents.beforeAdd, [obj])) {
			return;
		}
		if (Array.isArray(obj)) {
			return obj.map((element, key) => {
				if (key !== 0) {
					index = index + 1;
				}
				const id = this._addCore(element, index);
				this._onChange("add", element.id, element);
				this.events.fire(DataEvents.afterAdd, [element]);
				return id;
			});
		} else {
			const id = this._addCore(obj, index);
			this._onChange("add", obj.id, obj);
			this.events.fire(DataEvents.afterAdd, [obj]);
			return id;
		}
	}
	remove(id: Id | Id[]): void {
		if (id) {
			if (id instanceof Array) {
				id.map(element => {
					const obj = this._pull[element];
					if (obj) {
						if (!this.events.fire(DataEvents.beforeRemove, [obj])) {
							return;
						}
						this._removeCore(obj.id);
						this._onChange("remove", element, obj);
					}
					this.events.fire(DataEvents.afterRemove, [obj]);
				});
			} else {
				const obj = this._pull[id];
				if (obj) {
					if (!this.events.fire(DataEvents.beforeRemove, [obj])) {
						return;
					}
					this._removeCore(obj.id);
					this._onChange("remove", id, obj);
				}
				this.events.fire(DataEvents.afterRemove, [obj]);
			}
		}
	}
	removeAll(): void {
		this._removeAll();
		this.events.fire(DataEvents.removeAll);
		this.events.fire(DataEvents.change);
	}
	exists(id: Id): boolean {
		return !!this._pull[id];
	}
	getNearId(id: string){
		const item = this._pull[id];
		if (!item) {
			return this._order[0].id || "";
		}
	}
	getItem(id: Id): T {
		return this._pull[id];
	}
	update(id: Id, obj: IUpdateObject, silent?:boolean) {
		const item = this.getItem(id);
		if (item) {
			if (isEqualObj(obj, item)) {
				return;
			}

			if (obj.id && id !== obj.id) {
				dhxWarning("this method doesn't allow change id");
				if (isDebug()) {
					// tslint:disable-next-line:no-debugger
					debugger;
				}
			} else {
				extend(this._pull[id], obj, false);
				if (this.config.update){
					this.config.update(this._pull[id]);
				}
				if (!silent){
					this._onChange("update", id, this._pull[id]);
				}
			}
		} else {
			dhxWarning("item not found");
		}
	}
	getIndex(id: Id): number {
		const res = findIndex(this._order, item => item.id === id);
		if (this._pull[id] && res >= 0) {
			return res;
		}
		return -1;
	}
	getId(index: number): Id {
		if (!this._order[index]) {
			return;
		}
		return this._order[index].id;
	}
	getLength() {
		return this._order.length;
	}
	filter(rule?: IFilterMode | IFilterCallback, config?:IFilterConfig) {
		config = extend({
			add:false,
			multiple:true
		}, config);

		if (!config.add) {
			this._order = this._initOrder || this._order;
			this._initOrder = null;
		}

		this._filters = this._filters || {};

		if (!config.multiple || !rule) {
			this._filters = {};
		}

		if (rule) {
			if (typeof rule === "function") {
				const f = "_";
				this._filters[f] = {
					match:f,
					compare:rule
				};
			} else {
				if (!rule.match) {
					delete this._filters[rule.by];
				} else {
					rule.compare = rule.compare || ((val, match) => val === match);
					this._filters[rule.by] = rule;
				}
			}

			this._applyFilters();
		}

		this.events.fire(DataEvents.change);
	}
	find(conf: IFilterMode | DataCallback<T>): any {
		for (const key in this._pull) {
			const res = findByConf(this._pull[key], conf);
			if(res){
				return res;
			}
		}
		return null;
	}
	findAll(conf: IFilterMode | DataCallback<T>): any[] {
		const res = [];
		for (const key in this._pull) {
			const item = findByConf(this._pull[key], conf);
			if (item) {
				res.push(item);
			}
		}
		return res;
	}
	sort(by?: ISortMode): void {
		if (!by) {
			this._order = [];
			for (const key in this._pull) {
				this._order.push(this._pull[key]);
			}
			this._applyFilters();
		} else {
			this._sort.sort(this._order, by);

			if (this._initOrder && this._initOrder.length) {
				this._sort.sort(this._initOrder, by);
			}
		}

		this.events.fire(DataEvents.change);
	}
	copy(id: Id | Id[], index: number, target?: IDataCollection | ITreeCollection, targetId?: Id): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				if (!this.exists(elementId)) {
					return null;
				}
				const newid = uid();
				const elementIndex = index === -1 ? -1 : index + key;
				if (target) {
					if (!(target instanceof DataCollection) && targetId) {
						target.add(copyWithoutInner(this.getItem(elementId)), elementIndex);
						return;
					}
					if (target.exists(elementId)) {
						target.add({...copyWithoutInner(this.getItem(elementId)), id: newid }, elementIndex);
						return newid;
					} else {
						target.add(copyWithoutInner(this.getItem(elementId)), elementIndex);
						return elementId;
					}
				}
				this.add({ ...copyWithoutInner(this.getItem(elementId)), id: newid }, elementIndex);
				return newid;
			});
		} else {
			if (!this.exists(id)) {
				return null;
			}
			const newid = uid();
			if (target) {
				if (!(target instanceof DataCollection) && targetId) {
					target.add(copyWithoutInner(this.getItem(id)), index);
					return;
				}
				if (target.exists(id)) {
					target.add({...copyWithoutInner(this.getItem(id)), id: newid }, index);
					return newid;
				} else {
					target.add(copyWithoutInner(this.getItem(id)), index);
					return id;
				}
			}
			this.add({ ...copyWithoutInner(this.getItem(id)), id: newid }, index);
			return newid;
		}
	}
	move(id: Id | Id[], index, target?: DataCollection | TreeCollection, targetId?: Id): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				const elementIndex = index === -1 ? -1 : index + key;
				if (target && target !== this && this.exists(elementId)) {
					const item = copy(this.getItem(elementId), true);
					if (target.exists(elementId)) {
						item.id = uid();
					}
					if (targetId) {
						item.parent = targetId;
					}
					target.add(item, elementIndex);

					this.remove(elementId);
					return item.id;
				}
				if (this.getIndex(elementId) === elementIndex) {
					return null;
				}

				const spliced = this._order.splice(this.getIndex(elementId), 1)[0];
				if (index === -1) {
					index = this._order.length;
				}
				this._order.splice(elementIndex, 0, spliced);

				this.events.fire(DataEvents.change);
				return elementId;
			});
		} else {
			if (target && target !== this && this.exists(id)) {
				const item = copy(this.getItem(id), true);
				if (target.exists(id)) {
					item.id = uid();
				}
				if (targetId) {
					item.parent = targetId;
				}
				target.add(item, index);
				// remove data from original collection
				this.remove(id);
				return item.id;
			}
			if (this.getIndex(id) === index) {
				return null;
			}
			// move other elements
			const spliced = this._order.splice(this.getIndex(id), 1)[0];
			if (index === -1) {
				index = this._order.length;
			}
			this._order.splice(index, 0, spliced);

			this.events.fire(DataEvents.change); // if target not this, it trigger add and remove
			return id;
		}
	}
	load(url: IDataProxy | string, driver?: any): Promise<any> {
		if (typeof url === "string"){
			url = new DataProxy(url);
		}
		return this._loader.load(url, driver);
	}
	parse(data: T[], driver?: any) {
		this._removeAll();
		return this._loader.parse(data, driver);
	}
	$parse(data: any[]){
		const apx = this.config.approximate;
		if (apx){
			data = this._approximate(data, apx.value, apx.maxNum);
		}

		this._parse_data(data);

		this.events.fire(DataEvents.change, ["load"]);
		this.events.fire(DataEvents.load);
	}
	save(url: IDataProxy) {
		this._loader.save(url);
	}
	// todo: loop through the array and check saved statuses
	isSaved(): boolean {
		return !this._changes.order.length; // todo: bad solution, errors and holded elments are missed...
	}
	map(cb: DataCallback<T>) : any[]{
		const result : any[] = [];
		for (let i=0; i<this._order.length; i++){
			result.push(cb.call(this, this._order[i], i));
		}
		return result;
	}
	mapRange(from: number, to: number, cb: DataCallback<T>): any[] {
		if (from < 0) {
			from = 0;
		}
		if (to > this._order.length - 1) {
			to = this._order.length - 1;
		}
		const result: any[] = [];
		for (let i=from; i<=to; i++){
			result.push(cb.call(this, this._order[i], i));
		}
		return result;
	}
	reduce<A>(cb: ReduceCallBack<T, A>, acc: A){
		for (let i=0; i<this._order.length; i++) {
			acc = cb.call(this, acc, this._order[i], i);
		}
		return acc;
	}
	serialize(driver: DataDriver = DataDriver.json){ // remove $ attrs
		const data = this.map(item => {
			const newItem = {...item as IDataItem};
			Object.keys(newItem).forEach(key => {
				if (key[0] === "$") {
					delete newItem[key];
				}
			});
			return newItem;
		});
		const dataDriver = toDataDriver(driver);
		if(dataDriver){
			return dataDriver.serialize(data);
		}
	}
	getInitialData(){
		return this._initOrder;
	}
	protected _removeAll(){
		this._pull = {};
		this._order = [];
		this._changes.order = [];
		this._initOrder = null;
	}
	protected _addCore(obj, index): string {
		if (this.config.init) {
			obj = this.config.init(obj);
		}

		obj.id = obj.id ? obj.id.toString() : uid();

		if (this._pull[obj.id]) {
			dhxError("Item already exist");
		}
		// todo: not ideal solution
		if (this._initOrder && this._initOrder.length) {
			this._addToOrder(this._initOrder, obj, index);
		}

		this._addToOrder(this._order, obj, index);

		return obj.id;
	}
	protected _removeCore(id: Id){
		if (this.getIndex(id) >= 0){
			this._order = this._order.filter(el => el.id !== id);
			delete this._pull[id];
		}

		if (this._initOrder && this._initOrder.length) {
			this._initOrder = this._initOrder.filter(el => el.id !== id);
		}
	}

	protected _parse_data(data: any[]){
		let index = this._order.length;
		if(this.config.prep){
			data = this.config.prep(data);
		}
		for (let obj of data) {
			if (this.config.init) {
				obj = this.config.init(obj);
			}
			obj.id = (obj.id || obj.id === 0) ? obj.id : uid();
			this._pull[obj.id] = obj;
			this._order[index++] = obj;
		}
	}

	protected _approximate(data: any[], values:string[], maxNum:number){
		const len = data.length;
		const vlen = values.length;
		const rlen = Math.floor(len/maxNum);
		const newData = Array(Math.ceil(len/rlen));

		let index = 0;
		for (let i=0; i<len; i+=rlen) {
			const newItem = copy(data[i]);
			const end = Math.min(len, i+rlen);
			for (let j=0; j<vlen; j++) {
				let sum = 0;
				for (let z=i; z<end; z++){
					sum += data[z][values[j]];
				}
				newItem[values[j]] = sum / (end-i);
			}
			newData[index++] = newItem;
		}

		return newData;
	}
	protected _onChange(status: Statuses, id: Id, obj: any): void {
		for (let item of this._changes.order) {
			// update pending item if previous state is "saving" or if item not saved yet
			if (item.id === id && !item.saving) {
				// update item
				if (item.error) {
					item.error = false;
				}
				item = { ...item, obj, status };

				this.events.fire(DataEvents.change, [id, status, obj]);
				return;
			}
		}
		this._changes.order.push({ id, status, obj:{ ...obj}, saving: false });
		this.events.fire(DataEvents.change, [id, status, obj]);
	}
	protected _addToOrder(array: any[], obj: any, index?: number) {
		if (index >= 0 && array[index]) {
			this._pull[obj.id] = obj;
			array.splice(index, 0, obj);
		} else {
			this._pull[obj.id] = obj;
			array.push(obj);
		}
	}
	protected _applyFilters() {
		if (this._filters && Object.keys(this._filters).length) {
			const fOrder = this._order.filter(item => {
				return Object.keys(this._filters).every(
					key =>
						item[key]?
							this._filters[key].compare(item[key], this._filters[key].match, item)
							:this._filters[key].compare(item)
				);
			});
			if (!this._initOrder) {
				this._initOrder = this._order;
			}
			this._order = fOrder;
		}
	}
}